import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Room, SelectedRoom } from 'lib/types/trip';

import { AccomodationCardTitle } from './title';
import { AccomodationSuplement } from './supplement';
import { Dropdown } from 'components/Dropdown/Dropdown';
import { RoomIcon } from './room-icon';
import { RoomTip } from './tip';
import { TDropDownItem } from 'lib/types/dropdown';
import { useCurrency } from 'lib/hooks/useCurrency';
import { useTenant } from 'lib/hooks/useTenant';
import { useNumberFormatter } from 'lib/hooks/useNumberFormatter/useNumberFormatter';

type Props = {
  room: Room;
  travellerDropdown: TDropDownItem | null;
  setSelectedRooms: Dispatch<SetStateAction<SelectedRoom[]>>;
  selectedRooms: SelectedRoom[];
  defaultSelected: number;
  isRequired?: boolean;
  isFullyAssigned?: boolean;
};
const AccomodationCard = ({
  room,
  travellerDropdown,
  setSelectedRooms,
  selectedRooms,
  defaultSelected,
  isRequired = false,
  isFullyAssigned = false,
}: Props) => {
  const { isDA } = useTenant();
  const zeroLabel = isDA ? 'Select travellers' : '0 people';
  // DA: once everyone is assigned, empty options read "None". Derived at render
  // time so it flips live in both directions (selected value and menu list).
  const displayZeroLabel = isDA && isFullyAssigned ? 'None' : zeroLabel;
  const withZeroLabel = (item: TDropDownItem): TDropDownItem =>
    item.value === '0' ? { ...item, label: displayZeroLabel } : item;
  const [data, setData] = useState<TDropDownItem[] | null>(null);
  const [roomDropdown, setRoomDropdown] = useState<TDropDownItem | null>(null);
  const { currency, currencyShouldBeShown } = useCurrency();
  const numberFormatter = useNumberFormatter();

  const fillDropDown = () => {
    if (travellerDropdown) {
      const sumSelectedRooms = selectedRooms
        .map((item) => (item.id === room.id ? 0 : item?.count))
        .reduce((partialSum, a) => partialSum + a, 0);

      const array: TDropDownItem[] = Array.from(
        { length: parseInt(travellerDropdown?.value) - sumSelectedRooms + 1 },
        (v, k): TDropDownItem => {
          return {
            label: k === 0 ? zeroLabel : k.toString() + ' people',
            value: (k + 0).toString(),
          };
        }
      ).filter((item) =>
        parseInt(item.value) % room.capacity !== 0 && !room.can_share
          ? false
          : true
      );

      setData(array);
      return array;
    }
    return null;
  };

  useEffect(() => {
    if (travellerDropdown) {
      const array = fillDropDown();
      if (array) {
        const defaultDropDown: TDropDownItem | null =
          defaultSelected > 0
            ? {
                label: `${defaultSelected} people`,
                value: `${defaultSelected}`,
              }
            : array[0] ?? {
                label: zeroLabel,
                value: `0`,
              };

        setRoomDropdown(defaultDropDown);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travellerDropdown, defaultSelected]);

  useEffect(() => {
    fillDropDown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRooms]);

  useEffect(() => {
    if (roomDropdown) {
      const selectedRoom: SelectedRoom = {
        id: room.id,
        count: parseInt(roomDropdown.value),
        hasError:
          parseInt(roomDropdown.value) % room.capacity !== 0 && !room.can_share
            ? true
            : false,
      };

      setSelectedRooms((el) => {
        const roomIndex = el.findIndex((item) => item.id === room.id);
        if (roomIndex >= 0) {
          let newEl = JSON.parse(JSON.stringify(el));
          if (parseInt(roomDropdown.value) > 0) {
            newEl[roomIndex].count = parseInt(roomDropdown.value);
            newEl[roomIndex].hasError =
              parseInt(roomDropdown.value) % room.capacity !== 0 &&
              !room.can_share
                ? true
                : false;
          } else {
            newEl = newEl.filter(
              (item: any, index: number) => index !== roomIndex
            );
          }
          return newEl;
        } else if (parseInt(roomDropdown.value) > 0) {
          return [...el, selectedRoom];
        }
        return el;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomDropdown]);

  // DA: nothing to choose (only the 0 option left), so the dropdown is a read-only display
  const isOnlyZeroOption =
    !!data && data.length <= 1 && data.every((item) => item.value === '0');

  return (
    <div>
      <div className="border border-light-800 rounded-md p-4 md:px-5 md:pt-5 md:pb-0">
        <div
          className={
            isDA
              ? // DA: single-row layout only from lg, so the wider control column never squeezes the title
                'flex flex-col lg:flex-row lg:justify-between lg:items-center md:pb-5 '
              : 'flex flex-col md:flex-row md:justify-between md:items-center md:pb-5 '
          }
        >
          <div
            className={
              'flex flex-row gap-5 flex-grow pb-4 ' +
              (isDA ? 'lg:pb-0' : 'md:pb-0')
            }
          >
            <RoomIcon capacity={room.capacity} />
            <div
              className={
                isDA
                  ? // DA: leading-6 adds 0.1875rem above/below each title line, so the gap shrinks to keep the same visual spacing
                    'flex flex-col gap-[0.4375rem] md:max-w-[17.5rem]'
                  : 'flex flex-col gap-[0.625rem] md:max-w-[17.5rem]'
              }
            >
              <AccomodationCardTitle
                title={
                  (room.can_share ? 'Shared ' : '') +
                  (room.title || room.arr_name)
                }
                description={room.description}
                className={isDA ? 'leading-6' : ''}
              />
              {room.title && (
                <div className="font-semibold text-dark-500 text-sm">
                  {room.arr_name}
                </div>
              )}
            </div>
          </div>
          {room.single_supplement > 0 && (
            <div className=" md:hidden">
              <AccomodationSuplement />
            </div>
          )}
          <div
            className={
              isDA
                ? 'flex flex-wrap lg:flex-nowrap justify-start gap-4 lg:gap-[0.5rem] lg:pl-[0.5rem] border-t lg:border-t-0 lg:border-l border-light-700 items-center pt-4 lg:pt-0 lg:w-[23.5rem] lg:shrink-0'
                : 'flex justify-between md:pl-[0.5rem] gap-[0.5rem] border-t md:border-t-0 md:border-l border-light-700 items-center pt-4 md:pt-0 md:w-[18.75rem]'
            }
          >
            {room.is_included ? (
              <div
                className={`${
                  isDA ? 'text-sm' : 'text-xs'
                } text-green-900 font-semibold`}
              >
                Included
              </div>
            ) : (
              <div
                className={
                  isDA
                    ? 'flex items-center shrink-0 whitespace-nowrap'
                    : 'flex items-center'
                }
              >
                <div className="text-sm text-dark-700 font-semibold">
                  {numberFormatter.format(room.prices[currency])}
                </div>
                {currencyShouldBeShown ? (
                  <div className="text-xs text-dark-700 font-semibold ml-0.5 mb-0.5">
                    ({currency})
                  </div>
                ) : null}
                <div className="text-sm text-dark-700 font-semibold ml-1 ">
                  / person
                </div>
              </div>
            )}

            <div
              className={
                isDA
                  ? 'flex-1 min-w-[10.75rem] max-w-[13.5rem] ml-auto'
                  : 'w-[8.75rem]'
              }
            >
              {isDA && (
                <div className="mb-1 text-xs font-semibold text-dark-700 md:whitespace-nowrap">
                  Travellers in this accommodation
                </div>
              )}
              {data && (
                <Dropdown
                  disabled={isDA && isOnlyZeroOption}
                  options={data.map(withZeroLabel)}
                  selected={roomDropdown ? withZeroLabel(roomDropdown) : null}
                  setSelected={setRoomDropdown}
                  placeholder=""
                  hasBorder={!isRequired}
                  controllerClassName={
                    // DA: extra right padding keeps the value clear of the chevron
                    (isDA ? 'pr-10 ' : '') +
                    (isRequired ? 'border border-dealsaway-900' : '')
                  }
                  hasArrow={true}
                  hasSearch={false}
                  menuStretchesToController
                  menuClassName="!drop-shadow-xl"
                />
              )}
            </div>
          </div>
        </div>
        {room.single_supplement > 0 && (
          <div className="hidden md:block">
            <AccomodationSuplement />
          </div>
        )}
      </div>
      {roomDropdown &&
        parseInt(roomDropdown.value) % room.capacity !== 0 &&
        !room.can_share && (
          <div className="mt-[0.625rem]">
            <RoomTip
              title="Important"
              titleColor="red"
              text={`This accommodation is for ${room.capacity} people. Please fill the room or select another option.`}
              icon="notification"
            />
          </div>
        )}
    </div>
  );
};
export { AccomodationCard };
